import React, {
  type RefObject,
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { SecondaryButtonLightBg } from 'apps/sigma2-ui/src/components/SecondaryButtonLightBg';
import { TertiaryButton } from 'apps/sigma2-ui/src/components/TertiaryButton';
import CopyToClipboard from 'apps/sigma2-ui/src/components/ui/CopyToClipboard';
import { ScrollScrim } from 'apps/sigma2-ui/src/components/ui/ScrollScrim';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { LightModal } from 'apps/sigma2-ui/src/components/ui/LightModal';
import { OrgSwitcherContext } from 'apps/sigma2-ui/src/components/OrgSwitcherContext';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { CircularProgress } from '@mui/material';
import { Dropdown } from 'apps/sigma2-ui/src/components/Dropdown';
import { IconArrowTopSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowTop';
import { truncateTextIfNecessary } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { IconArrowLeft24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowLeft24px';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { IconBuildingSvg } from 'apps/sigma2-ui/src/components/svg/IconBuilding';
import { IconGlobeNetworkSvg } from 'apps/sigma2-ui/src/components/svg/IconGlobeNetwork';
import { IconLink16Svg } from 'apps/sigma2-ui/src/components/svg/IconLink16';
import { IconShareSvg } from 'apps/sigma2-ui/src/components/svg/IconShare';
import { ExternalShareWarningIconSvg } from 'apps/sigma2-ui/src/components/svg/ExternalShareWarningIcon';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { isEntitledToPublishDashboards } from 'libs/end-to-end/src/group-entitlements';
import cn from 'classnames';
import { DashboardExternalUserDto } from 'libs/end-to-end/src/dashboard';
import { DashboardExternalUsersDto } from 'libs/end-to-end/src/dashboard';
import { dashboardExternalInviteMessageMaxLength } from 'libs/end-to-end/src/dashboard';
import { DraftDashboard } from 'libs/end-to-end/src/dashboard';
import { InviteDashboardExternalUsersRequestDto } from 'libs/end-to-end/src/dashboard';
import { InviteDashboardExternalUsersResponseDto } from 'libs/end-to-end/src/dashboard';
import { ResendDashboardExternalUserInvitationRequestDto } from 'libs/end-to-end/src/dashboard';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { IconDownloadSvg } from 'apps/sigma2-ui/src/components/svg/IconDownload';
import { IconDocumentFileSvg } from 'apps/sigma2-ui/src/components/svg/IconDocumentFile';
import { ExternalLinkSvg } from 'apps/sigma2-ui/src/components/svg/ExternalLinkSvg';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSelectedOrganizationId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedOrganizationId';
import { useSelectedDatabaseId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseId';
import { useSelectedDatabaseRevisionId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseRevisionId';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { isInviteExternalUsersToDashboardsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPublishDashboardsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';

const getInitialsFromEmail = (email: string) => {
  return email
    .split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

const isValidExternalInviteEmail = (email: string) => {
  return DashboardExternalUserDto.shape.email.safeParse(email.trim()).success;
};

type ShareFlowMode = 'default' | 'compose' | 'confirm';
type PublishModalTab = 'share' | 'publish';

const resolveBaseErrorMessage = (
  error: BaseError | undefined,
  fallbackMessage: string,
) => {
  return typeof error?.errorReason === 'string'
    ? error.errorReason
    : fallbackMessage;
};

const getDashboardExternalInviteWarning = ({
  failedEmails,
  language,
}: {
  failedEmails: string[];
  language: ReturnType<typeof useLanguage>;
}) => {
  const emails = failedEmails.join(', ');

  return `${language.dashboardExternalInviteWarningPrefix()} ${emails}`;
};

const useExternalInviteFlow = () => {
  const [shareFlowMode, setShareFlowMode] = useState<ShareFlowMode>('default');
  const [externalInviteDraft, setExternalInviteDraft] = useState('');
  const [externalInviteEmails, setExternalInviteEmails] = useState<string[]>(
    [],
  );
  const [externalInviteFocused, setExternalInviteFocused] = useState(false);
  const [externalInviteMessage, setExternalInviteMessage] = useState('');
  const externalInviteInputRef = useRef<HTMLInputElement | null>(null);

  const commitExternalInviteEmail = useCallback((value: string) => {
    const normalizedValue = value.trim().replace(/[,\s;]+$/g, '');

    if (!normalizedValue) {
      return false;
    }

    setExternalInviteEmails((previousEmails) => {
      const alreadyAdded = previousEmails.some(
        (email) => email.toLowerCase() === normalizedValue.toLowerCase(),
      );

      if (alreadyAdded) {
        return previousEmails;
      }

      return [...previousEmails, normalizedValue];
    });
    setExternalInviteDraft('');

    return true;
  }, []);

  const handleExternalInviteKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        [' ', 'Enter', 'Tab', ','].includes(event.key) &&
        externalInviteDraft.trim()
      ) {
        const committed =
          event.key === ' '
            ? commitExternalInviteEmail(externalInviteDraft)
            : isValidExternalInviteEmail(
                externalInviteDraft.trim().replace(/[,\s;]+$/g, ''),
              ) && commitExternalInviteEmail(externalInviteDraft);

        if (committed) {
          event.preventDefault();
        }

        return;
      }

      if (
        event.key === 'Backspace' &&
        !externalInviteDraft &&
        externalInviteEmails.length > 0
      ) {
        event.preventDefault();
        setExternalInviteEmails((previousEmails) => {
          return previousEmails.slice(0, -1);
        });
      }
    },
    [
      commitExternalInviteEmail,
      externalInviteDraft,
      externalInviteEmails.length,
    ],
  );

  const handleExternalInviteBlur = useCallback(() => {
    setExternalInviteFocused(false);
    void commitExternalInviteEmail(externalInviteDraft);
  }, [commitExternalInviteEmail, externalInviteDraft]);

  const handleExternalInvitePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = event.clipboardData.getData('text');
      const parsedValues = pastedText
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      const validEmails = parsedValues.filter((value) =>
        isValidExternalInviteEmail(value),
      );

      if (parsedValues.length <= 1) {
        return;
      }

      event.preventDefault();

      validEmails.forEach((email) => {
        void commitExternalInviteEmail(email);
      });
    },
    [commitExternalInviteEmail],
  );

  const hasInvalidExternalInviteEmail = useMemo(() => {
    return (
      externalInviteEmails.some(
        (email) => !isValidExternalInviteEmail(email),
      ) ||
      (externalInviteDraft.trim().length > 0 &&
        !isValidExternalInviteEmail(externalInviteDraft))
    );
  }, [externalInviteDraft, externalInviteEmails]);

  const isExternalInviteShareDisabled = useMemo(() => {
    return externalInviteEmails.length === 0 || hasInvalidExternalInviteEmail;
  }, [externalInviteEmails.length, hasInvalidExternalInviteEmail]);

  const hasAtLeastOneExternalInviteEmail = useMemo(() => {
    return externalInviteEmails.length > 0;
  }, [externalInviteEmails.length]);

  const handleStartExternalInviteComposer = useCallback(() => {
    setExternalInviteFocused(true);
  }, []);

  const isShowingExternalInviteComposePanel = useMemo(() => {
    return shareFlowMode !== 'confirm' && hasAtLeastOneExternalInviteEmail;
  }, [hasAtLeastOneExternalInviteEmail, shareFlowMode]);

  const handleProceedToExternalInviteConfirmation = useCallback(() => {
    const normalizedDraft = externalInviteDraft.trim();

    if (normalizedDraft && isValidExternalInviteEmail(normalizedDraft)) {
      void commitExternalInviteEmail(normalizedDraft);
    }

    setShareFlowMode('confirm');
  }, [commitExternalInviteEmail, externalInviteDraft]);

  const handleRemoveExternalInviteEmail = useCallback((email: string) => {
    setExternalInviteEmails((previousEmails) => {
      return previousEmails.filter((previousEmail) => previousEmail !== email);
    });
    externalInviteInputRef.current?.focus();
  }, []);

  const resetExternalInviteFlow = useCallback(() => {
    setExternalInviteDraft('');
    setExternalInviteEmails([]);
    setExternalInviteFocused(false);
    setShareFlowMode('default');
    setExternalInviteMessage('');
  }, []);

  const hideExternalInviteComposer = useCallback(() => {
    setExternalInviteFocused(false);
    setShareFlowMode('default');
  }, []);

  return {
    shareFlowMode,
    setShareFlowMode,
    externalInviteDraft,
    setExternalInviteDraft,
    externalInviteEmails,
    setExternalInviteEmails,
    externalInviteFocused,
    externalInviteMessage,
    setExternalInviteMessage,
    externalInviteInputRef,
    hasInvalidExternalInviteEmail,
    isExternalInviteShareDisabled,
    isShowingExternalInviteComposePanel,
    handleExternalInviteKeyDown,
    handleExternalInviteBlur,
    handleExternalInvitePaste,
    handleStartExternalInviteComposer,
    handleProceedToExternalInviteConfirmation,
    handleRemoveExternalInviteEmail,
    resetExternalInviteFlow,
    hideExternalInviteComposer,
  };
};

const PublishModalTabs = ({
  activeTab,
  showPublishTab,
  onSelectTab,
}: {
  activeTab: PublishModalTab;
  showPublishTab: boolean;
  onSelectTab: (tab: PublishModalTab) => void;
}) => {
  const language = useLanguage();
  const tabs = [
    { key: 'share' as const, label: language.shareTab() },
    ...(showPublishTab
      ? [{ key: 'publish' as const, label: language.publishTab() }]
      : []),
  ];
  const activeTabIndex = tabs.findIndex((tab) => tab.key === activeTab);

  return (
    <div className="flex w-full items-start">
      {tabs.map((tab, index) => {
        const isActive = activeTab === tab.key;
        const inactiveShadowClass =
          index < activeTabIndex
            ? 'shadow-[inset_-2px_-2px_4px_0px_rgba(0,0,0,0.08)]'
            : 'shadow-[inset_2px_-2px_4px_0px_rgba(0,0,0,0.08)]';

        return (
          <button
            key={tab.key}
            type="button"
            data-testid={`publish-modal-tab-${tab.key}`}
            className={cn(
              'relative flex h-[60px] flex-1 items-center justify-center overflow-hidden bg-white text-center',
              !isActive && inactiveShadowClass,
            )}
            onClick={() => {
              onSelectTab(tab.key);
            }}
          >
            {isActive && (
              <div className="absolute left-0 right-0 top-0 h-1 bg-royal-700" />
            )}
            <S2Typography
              translatedContent={tab.label}
              variant="headline-6-alt"
              textColorVariant={isActive ? 'royal-800' : 'gray-600'}
            />
          </button>
        );
      })}
    </div>
  );
};

const ExternalInviteTokenInput = ({
  emails,
  draft,
  focused,
  disabled,
  hasValidationError,
  inputRef,
  onContainerClick,
  onDraftChange,
  onFocus,
  onBlur,
  onKeyDown,
  onPaste,
  onRemoveEmail,
}: {
  emails: string[];
  draft: string;
  focused: boolean;
  disabled: boolean;
  hasValidationError: boolean;
  inputRef: RefObject<HTMLInputElement>;
  onContainerClick: () => void;
  onDraftChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  onRemoveEmail: (email: string) => void;
}) => {
  const language = useLanguage();

  return (
    <div
      className={cn(
        'flex min-h-10 max-h-32 w-full cursor-text overflow-y-auto rounded-[4px] border-[1.5px] bg-white px-4 py-2 transition',
        disabled && 'cursor-not-allowed opacity-60',
        hasValidationError
          ? 'border-red-600'
          : focused
            ? 'border-royal-700'
            : 'border-gray-300 hover:border-gray-400',
      )}
      onClick={() => {
        if (disabled) {
          return;
        }

        onContainerClick();
      }}
      data-qa="dashboardShareExternalInviteInput"
    >
      <div className="flex w-full min-w-0 flex-1 flex-wrap content-start items-center gap-1">
        {emails.map((email) => (
          <div
            key={email}
            className="flex min-w-0 max-w-full items-center gap-1 rounded-[4px] bg-gray-100 px-1 py-0.5"
          >
            <S2Typography
              as="span"
              translatedContent={email}
              variant="body-7"
              textColorVariant="gray-1000"
              dangerouslyOverrideClassNameFn={(className) =>
                cn(
                  className,
                  'min-w-0 max-w-[280px] truncate text-sm leading-6',
                )
              }
            />
            <TouchTarget
              behavior={{
                type: 'onClick',
                onClick: () => {
                  if (disabled) {
                    return;
                  }

                  onRemoveEmail(email);
                },
              }}
              className="flex shrink-0 items-center justify-center text-gray-1000"
              content={() => <IconCloseSvg svg={{ className: 'h-4 w-4' }} />}
              label={`${language.remove()} ${email}`}
            />
          </div>
        ))}
        <input
          ref={inputRef}
          type="email"
          maxLength={1000}
          value={draft}
          disabled={disabled}
          placeholder={
            emails.length === 0
              ? language.addPeopleOutsideOrganizationPlaceholder()
              : ''
          }
          className="my-1 min-w-[120px] flex-1 border-none bg-transparent p-0 text-sm text-gray-1000 placeholder:text-gray-600 disabled:cursor-not-allowed disabled:text-gray-700 focus:outline-none focus:ring-0"
          onChange={(event) => {
            onDraftChange(event.currentTarget.value);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
    </div>
  );
};

const ExternalInviteComposePanel = ({
  message,
  shareDisabled,
  onMessageChange,
  onShare,
}: {
  message: string;
  shareDisabled: boolean;
  onMessageChange: (value: string) => void;
  onShare: () => void;
}) => {
  const language = useLanguage();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex min-h-0 flex-1 flex-col gap-1">
        <S2Typography
          translatedContent={language.messageOptional()}
          variant="body-7"
          textColorVariant="gray-900"
        />
        <div className="relative flex min-h-0 flex-1">
          <textarea
            value={message}
            placeholder={language.enterMessagePlaceholder()}
            maxLength={dashboardExternalInviteMessageMaxLength}
            className="form-textarea flex-1 min-h-[240px] w-full resize-none rounded border border-gray-200 px-3 pt-2 pb-7 text-sm leading-6 text-gray-1000 placeholder:text-gray-500 focus:border-indigo-200 focus:ring-2 focus:ring-indigo-200"
            onChange={(event) => {
              onMessageChange(event.currentTarget.value);
            }}
          />
          <S2Typography
            translatedContent={`${message.length}/${dashboardExternalInviteMessageMaxLength}`}
            variant="small-label"
            textColorVariant="gray-600"
            dangerouslyOverrideClassNameFn={(className) =>
              cn(
                className,
                'pointer-events-none absolute bottom-2 right-6 select-none rounded bg-white/90 px-1',
              )
            }
          />
        </div>
      </div>
      <PrimaryButtonLightBg
        className="w-full !bg-royal-800 !text-white hover:!bg-royal-700"
        translatedContent={language.share()}
        onClick={onShare}
        disabled={shareDisabled}
      />
    </div>
  );
};

const ExternalInviteConfirmationPanel = ({
  dashboardName,
  isSharing,
  onGoBack,
  onPreviewGuestDashboard,
  onShareAnyways,
}: {
  dashboardName: string;
  isSharing: boolean;
  onGoBack: () => void;
  onPreviewGuestDashboard: () => void;
  onShareAnyways: () => void;
}) => {
  const language = useLanguage();

  return (
    <div className="flex flex-1 flex-col justify-between gap-8 px-4">
      <div className="flex flex-1 flex-col items-center justify-center gap-8">
        <div className="flex h-[72px] w-[72px] items-center justify-center rounded-[20px] bg-[#080857]">
          <ExternalShareWarningIconSvg />
        </div>

        <div className="flex w-full max-w-[360px] flex-col items-center gap-4">
          <S2Typography
            translatedContent={language.shareWithExternalWarningTitle()}
            variant="headline-5-alt"
            textColorVariant="gray-1000"
            dangerouslyOverrideClassNameFn={(className) =>
              cn(className, 'text-center')
            }
          />

          <S2Typography
            translatedContent={language.shareWithExternalWarningBody({
              dashboardName,
            })}
            variant="body-7"
            textColorVariant="gray-600"
            dangerouslyOverrideClassNameFn={(className) =>
              cn(className, 'text-center text-sm leading-6')
            }
          />

          <TouchTarget
            behavior={{
              type: 'onClick',
              onClick: () => {
                onPreviewGuestDashboard();
              },
            }}
            content={() => (
              <div className="flex items-center gap-1">
                <ExternalLinkSvg
                  svg={{ className: 'h-4 w-4 text-royal-700' }}
                  wantsCurrentColor
                />
                <S2Typography
                  translatedContent={language.previewGuestDashboard()}
                  variant="headline-7-alt"
                  textColorVariant="royal-700"
                />
              </div>
            )}
          />
        </div>
      </div>

      <div className="flex w-full gap-4">
        <SecondaryButtonLightBg
          className="flex-1"
          translatedContent={language.goBack()}
          onClick={onGoBack}
          disabled={isSharing}
        />
        <PrimaryButtonLightBg
          className="flex-1"
          icon={() =>
            isSharing ? <Spinner size={17} textColor="text-white" /> : undefined
          }
          translatedContent={language.shareAnyways()}
          onClick={onShareAnyways}
          disabled={isSharing}
        />
      </div>
    </div>
  );
};

const ExternalUsersSection = ({
  status,
  users,
  ownerEmail,
  resendingUserId,
  removingUserIds,
  onOpenGuestDashboard,
  onResend,
  onRemove,
}: {
  status: 'loading' | 'error' | 'done';
  users: DashboardExternalUsersDto;
  ownerEmail: string | null;
  resendingUserId: string | null;
  removingUserIds: string[];
  onOpenGuestDashboard: () => void;
  onResend: (dashboardExternalUserId: string) => void;
  onRemove: (dashboardExternalUserId: string) => void;
}) => {
  const language = useLanguage();
  const [openDropdownUserId, setOpenDropdownUserId] = useState<string | null>(
    null,
  );

  return (
    <div className="flex flex-col gap-5">
      <S2Typography
        translatedContent={language.peopleWithAccess()}
        variant="headline-6-alt"
        textColorVariant="gray-1000"
      />

      <div className="flex flex-col gap-4">
        {ownerEmail ? (
          <div className="flex items-center justify-between gap-8">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-royal-200">
                <S2Typography
                  translatedContent={getInitialsFromEmail(ownerEmail)}
                  variant="headline-7-alt"
                  textColorVariant="royal-900"
                />
              </div>
              <div className="flex min-w-0 flex-col">
                <S2Typography
                  translatedContent={ownerEmail}
                  variant="headline-6-alt"
                  textColorVariant="gray-1000"
                />
                <S2Typography
                  translatedContent={ownerEmail}
                  variant="body-7"
                  textColorVariant="gray-600"
                  dangerouslyOverrideClassNameFn={(className) =>
                    cn(className, 'truncate')
                  }
                />
              </div>
            </div>
            <div className="flex shrink-0">
              <S2Typography
                translatedContent={'Owner'}
                variant="headline-7-alt"
                textColorVariant="indigo-900"
                dangerouslyOverrideClassNameFn={(className) =>
                  cn(className, 'opacity-[0.64]')
                }
              />
            </div>
          </div>
        ) : null}

        {status === 'loading' ? (
          <div className="flex items-center gap-2">
            <CircularProgress size={16} />
            <S2Typography
              translatedContent={language.loadingPeopleWithAccess()}
              variant="body-7"
              textColorVariant="gray-600"
            />
          </div>
        ) : status === 'error' ? (
          <S2Typography
            translatedContent={language.unableToLoadPeopleWithAccess()}
            variant="body-7"
            textColorVariant="red-600"
          />
        ) : users.length === 0 ? (
          !ownerEmail ? (
            <S2Typography
              translatedContent={language.noPeopleWithAccessYet()}
              variant="body-7"
              textColorVariant="gray-600"
            />
          ) : null
        ) : (
          <>
            {users.map((person) => {
              const viewerDropdownItems = [
                {
                  key: 'viewer',
                  content: (
                    <S2Typography
                      translatedContent={language.viewer()}
                      variant="body-7-medium"
                      textColorVariant="gray-800"
                      dangerouslyOverrideClassNameFn={(className) =>
                        cn(className, 'text-sm leading-[21px]')
                      }
                    />
                  ),
                  behavior: {
                    type: 'onClick' as const,
                    onClick: () => {},
                  },
                },
                {
                  key: 'resend',
                  closeOnClick: false,
                  content: (
                    <S2Typography
                      translatedContent={
                        resendingUserId === person.id
                          ? language.resending()
                          : language.resend()
                      }
                      variant="body-7-medium"
                      textColorVariant="gray-800"
                      dangerouslyOverrideClassNameFn={(className) =>
                        cn(className, 'text-sm leading-[21px]')
                      }
                    />
                  ),
                  behavior: {
                    type: 'onClick' as const,
                    onClick: () => {
                      onResend(person.id);
                    },
                  },
                },
                {
                  key: 'remove',
                  content: (
                    <S2Typography
                      translatedContent={
                        removingUserIds.includes(person.id)
                          ? language.removing()
                          : language.remove()
                      }
                      variant="body-7-medium"
                      textColorVariant="gray-800"
                      dangerouslyOverrideClassNameFn={(className) =>
                        cn(className, 'text-sm leading-[21px]')
                      }
                    />
                  ),
                  behavior: {
                    type: 'onClick' as const,
                    onClick: () => {
                      onRemove(person.id);
                    },
                  },
                },
              ];

              return (
                <div
                  key={person.id}
                  className="flex items-center justify-between gap-8"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E7E8E8]">
                      <div className="flex items-center justify-center rounded-full border border-dashed border-gray-300 p-2">
                        <PersonOutlineIcon className="!h-4 !w-4 text-gray-600" />
                      </div>
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <div className="flex min-w-0 items-baseline gap-1">
                        <S2Typography
                          translatedContent={person.email}
                          variant="headline-6-alt"
                          textColorVariant="gray-1000"
                          dangerouslyOverrideClassNameFn={(className) =>
                            cn(className, 'min-w-0 truncate')
                          }
                        />
                        <S2Typography
                          translatedContent={language.guestLabel()}
                          variant="body-7"
                          textColorVariant="gray-1000"
                          dangerouslyOverrideClassNameFn={(className) =>
                            cn(className, 'shrink-0 whitespace-nowrap')
                          }
                        />
                      </div>
                      <S2Typography
                        translatedContent={person.email}
                        variant="body-7"
                        textColorVariant="gray-600"
                        dangerouslyOverrideClassNameFn={(className) =>
                          cn(className, 'min-w-0 truncate')
                        }
                      />
                    </div>
                  </div>
                  <div className="flex shrink-0">
                    <Dropdown
                      trigger={
                        <div className="flex items-center gap-1">
                          <S2Typography
                            translatedContent={language.viewer()}
                            variant="headline-7-alt"
                            textColorVariant="indigo-900"
                          />
                          <IconArrowTopSvg
                            svg={{
                              className: 'h-4 w-4 rotate-180 text-indigo-900',
                            }}
                          />
                        </div>
                      }
                      items={viewerDropdownItems}
                      isOpen={openDropdownUserId === person.id}
                      setIsOpen={(isOpen) => {
                        setOpenDropdownUserId(isOpen ? person.id : null);
                      }}
                      innerClassName="!px-0"
                    />
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 rounded-[8px] bg-gray-100 px-4 py-3">
        <S2Typography
          translatedContent={language.guestsHaveViewOnlyAccess()}
          variant="body-7"
          textColorVariant="gray-1000"
        />
        <TouchTarget
          className="shrink-0"
          behavior={{
            type: 'onClick',
            onClick: onOpenGuestDashboard,
          }}
          content={() => (
            <div className="flex items-center gap-1">
              <ExternalLinkSvg
                svg={{ className: 'h-4 w-4 text-royal-700' }}
                wantsCurrentColor
              />
              <S2Typography
                translatedContent={language.open()}
                variant="headline-7-alt"
                textColorVariant="royal-700"
              />
            </div>
          )}
        />
      </div>
    </div>
  );
};

const GeneralAccessSection = ({
  generalAccessTrigger,
  language,
}: {
  generalAccessTrigger: React.ReactElement;
  language: ReturnType<typeof useLanguage>;
}) => {
  return (
    <div className="flex flex-col" data-testid="general-access-section">
      <S2Typography
        translatedContent={language.generalAccess()}
        variant="headline-6-alt"
        textColorVariant="gray-1000"
      />
      <div className="mt-4 flex items-center justify-between gap-8">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-royal-200">
            <IconBuildingSvg />
          </div>
          <div className="flex min-w-0 flex-col">
            <div
              className="flex min-w-0 w-full"
              data-testid="general-access-dropdown"
            >
              {generalAccessTrigger}
            </div>
            <S2Typography
              translatedContent={language.anyoneInThisOrganizationCanEdit()}
              variant="body-7"
              textColorVariant="gray-600"
            />
          </div>
        </div>
        <div className="flex shrink-0">
          <S2Typography
            translatedContent={language.canEdit()}
            variant="headline-7-alt"
            textColorVariant="indigo-900"
            dangerouslyOverrideClassNameFn={(className) =>
              cn(className, 'opacity-[0.64]')
            }
          />
        </div>
      </div>
    </div>
  );
};

const ShareLinkFooter = ({
  linkToCopy,
  copyToastText,
}: {
  linkToCopy: string;
  copyToastText: string;
}) => {
  const language = useLanguage();

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <LightTooltip
          arrow
          title={
            <div className="max-w-[256px] text-[14px] font-normal leading-[21px] tracking-[-0.14px] text-gray-1000">
              {language.linkSharingPermissionsTooltipPrefix()}
              <span className="font-bold tracking-[-0.14px]">
                {language.publish()}
              </span>
              {language.linkSharingPermissionsTooltipSuffix()}
            </div>
          }
        >
          <span className="inline-flex">
            <IconInformationSvg svg={{ className: 'h-4 w-4 text-gray-600' }} />
          </span>
        </LightTooltip>
        <S2Typography
          translatedContent={language.linkSharingPermissions()}
          variant="body-7"
          textColorVariant="gray-600"
        />
      </div>
      <CopyToClipboard
        textToCopy={linkToCopy}
        icon={<IconLink16Svg wantsCurrentColor={true} />}
        classNameStyle="shrink-0"
        toastStyle="custom"
        successMessage={copyToastText}
        autoHideDuration={2500}
        renderTrigger={({ onClick }) => (
          <TertiaryButton
            label={language.copyLink()}
            disabled={false}
            className="shrink-0 rounded-[4px] border-[1.5px] border-current px-3 py-2"
            typographyClassName="!text-sm font-bold leading-[21px]"
            behavior={{
              type: 'onClick',
              onClick,
            }}
            icon={<IconLink16Svg wantsCurrentColor={true} />}
          />
        )}
      />
    </div>
  );
};

const ShareAccessSections = ({
  externalUsersStatus,
  externalUsers,
  ownerEmail,
  showPeopleWithAccessSection,
  resendingExternalUserId,
  removingExternalUserIds,
  onOpenGuestDashboard,
  onResendExternalUser,
  onRemoveExternalUser,
  generalAccessTrigger,
  language,
}: {
  externalUsersStatus: 'loading' | 'error' | 'done';
  externalUsers: DashboardExternalUsersDto;
  ownerEmail: string | null;
  showPeopleWithAccessSection: boolean;
  resendingExternalUserId: string | null;
  removingExternalUserIds: string[];
  onOpenGuestDashboard: () => void;
  onResendExternalUser: (dashboardExternalUserId: string) => void;
  onRemoveExternalUser: (dashboardExternalUserId: string) => void;
  generalAccessTrigger: React.ReactElement;
  language: ReturnType<typeof useLanguage>;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showBottomScrim, setShowBottomScrim] = useState(false);

  const updateBottomScrimVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      setShowBottomScrim(false);
      return;
    }

    const hasOverflow = container.scrollHeight > container.clientHeight + 1;
    const isAtBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 1;

    setShowBottomScrim(hasOverflow && !isAtBottom);
  }, []);

  useEffect(() => {
    const animationFrameId = window.requestAnimationFrame(() => {
      updateBottomScrimVisibility();
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [updateBottomScrimVisibility, externalUsers]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateBottomScrimVisibility();
    });

    resizeObserver.observe(container);
    Array.from(container.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateBottomScrimVisibility, externalUsers]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-8">
      {showPeopleWithAccessSection ? (
        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto"
            onScroll={updateBottomScrimVisibility}
          >
            <ExternalUsersSection
              status={externalUsersStatus}
              users={externalUsers}
              ownerEmail={ownerEmail}
              resendingUserId={resendingExternalUserId}
              removingUserIds={removingExternalUserIds}
              onOpenGuestDashboard={onOpenGuestDashboard}
              onResend={onResendExternalUser}
              onRemove={onRemoveExternalUser}
            />
          </div>
          <ScrollScrim visible={showBottomScrim} direction="bottom" />
        </div>
      ) : null}
      <GeneralAccessSection
        generalAccessTrigger={generalAccessTrigger}
        language={language}
      />
    </div>
  );
};

const ShareTabContent = ({
  shareState,
  inviteInput,
  accessSections,
  shareActions,
}: {
  shareState: {
    flowMode: ShareFlowMode;
    dashboardName: string;
    canInviteExternalUsers: boolean;
    isSharingExternalUsers: boolean;
    isShowingComposePanel: boolean;
    message: string;
    isShareDisabled: boolean;
  };
  inviteInput: {
    emails: string[];
    draft: string;
    focused: boolean;
    hasValidationError: boolean;
    inputRef: RefObject<HTMLInputElement>;
    onStartComposer: () => void;
    onDraftChange: (value: string) => void;
    onBlur: () => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => void;
    onRemoveEmail: (email: string) => void;
  };
  accessSections: {
    externalUsersStatus: 'loading' | 'error' | 'done';
    activeExternalUsers: DashboardExternalUsersDto;
    showPeopleWithAccessSection: boolean;
    ownerEmail: string | null;
    resendingExternalUserId: string | null;
    removingExternalUserIds: string[];
    onOpenGuestDashboard: () => void;
    generalAccessTrigger: React.ReactElement;
    language: ReturnType<typeof useLanguage>;
    onResendExternalUser: (dashboardExternalUserId: string) => void;
    onRemoveExternalUser: (dashboardExternalUserId: string) => void;
  };
  shareActions: {
    onGoBackFromConfirmation: () => void;
    onPreviewGuestDashboard: () => void;
    onShareExternalUsers: () => void;
    onMessageChange: (value: string) => void;
    onProceedToConfirmation: () => void;
    copyDashboardLink: string;
    copyToastText: string;
  };
}) => {
  if (!shareState.canInviteExternalUsers) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <ShareAccessSections
          externalUsersStatus={accessSections.externalUsersStatus}
          externalUsers={accessSections.activeExternalUsers}
          ownerEmail={accessSections.ownerEmail}
          showPeopleWithAccessSection={
            accessSections.showPeopleWithAccessSection
          }
          resendingExternalUserId={accessSections.resendingExternalUserId}
          removingExternalUserIds={accessSections.removingExternalUserIds}
          onOpenGuestDashboard={accessSections.onOpenGuestDashboard}
          onResendExternalUser={accessSections.onResendExternalUser}
          onRemoveExternalUser={accessSections.onRemoveExternalUser}
          generalAccessTrigger={accessSections.generalAccessTrigger}
          language={accessSections.language}
        />
        <ShareLinkFooter
          linkToCopy={shareActions.copyDashboardLink}
          copyToastText={shareActions.copyToastText}
        />
      </div>
    );
  }

  if (shareState.flowMode === 'confirm') {
    return (
      <ExternalInviteConfirmationPanel
        dashboardName={shareState.dashboardName}
        isSharing={shareState.isSharingExternalUsers}
        onGoBack={shareActions.onGoBackFromConfirmation}
        onPreviewGuestDashboard={shareActions.onPreviewGuestDashboard}
        onShareAnyways={shareActions.onShareExternalUsers}
      />
    );
  }

  if (shareState.isShowingComposePanel) {
    return (
      <>
        <ExternalInviteTokenInput
          emails={inviteInput.emails}
          draft={inviteInput.draft}
          focused={inviteInput.focused}
          disabled={false}
          hasValidationError={inviteInput.hasValidationError}
          inputRef={inviteInput.inputRef}
          onContainerClick={() => {
            inviteInput.onStartComposer();
            inviteInput.inputRef.current?.focus();
          }}
          onDraftChange={inviteInput.onDraftChange}
          onFocus={inviteInput.onStartComposer}
          onBlur={inviteInput.onBlur}
          onKeyDown={inviteInput.onKeyDown}
          onPaste={inviteInput.onPaste}
          onRemoveEmail={inviteInput.onRemoveEmail}
        />
        <ExternalInviteComposePanel
          message={shareState.message}
          shareDisabled={shareState.isShareDisabled}
          onMessageChange={shareActions.onMessageChange}
          onShare={shareActions.onProceedToConfirmation}
        />
      </>
    );
  }

  return (
    <>
      <ExternalInviteTokenInput
        emails={inviteInput.emails}
        draft={inviteInput.draft}
        focused={inviteInput.focused}
        disabled={false}
        hasValidationError={inviteInput.hasValidationError}
        inputRef={inviteInput.inputRef}
        onContainerClick={() => {
          inviteInput.onStartComposer();
          inviteInput.inputRef.current?.focus();
        }}
        onDraftChange={inviteInput.onDraftChange}
        onFocus={inviteInput.onStartComposer}
        onBlur={inviteInput.onBlur}
        onKeyDown={inviteInput.onKeyDown}
        onPaste={inviteInput.onPaste}
        onRemoveEmail={inviteInput.onRemoveEmail}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <ShareAccessSections
          externalUsersStatus={accessSections.externalUsersStatus}
          externalUsers={accessSections.activeExternalUsers}
          ownerEmail={accessSections.ownerEmail}
          showPeopleWithAccessSection={
            accessSections.showPeopleWithAccessSection
          }
          resendingExternalUserId={accessSections.resendingExternalUserId}
          removingExternalUserIds={accessSections.removingExternalUserIds}
          onOpenGuestDashboard={accessSections.onOpenGuestDashboard}
          onResendExternalUser={accessSections.onResendExternalUser}
          onRemoveExternalUser={accessSections.onRemoveExternalUser}
          generalAccessTrigger={accessSections.generalAccessTrigger}
          language={accessSections.language}
        />
        <ShareLinkFooter
          linkToCopy={shareActions.copyDashboardLink}
          copyToastText={shareActions.copyToastText}
        />
      </div>
    </>
  );
};

const PublishDashboardDraftState = ({
  isSaving,
  onPreview,
  onPublish,
}: {
  isSaving: boolean;
  onPreview: () => void;
  onPublish: () => void;
}) => {
  const language = useLanguage();

  return (
    <div className="flex flex-1 flex-col items-center justify-between gap-8 px-4 py-4">
      <div className="flex flex-1 flex-col items-center justify-center gap-8">
        <div className="flex h-[72px] w-[72px] items-center justify-center rounded-[20px] bg-indigo-1000">
          <IconGlobeNetworkSvg svg={{ className: 'h-8 w-8' }} />
        </div>
        <div className="flex max-w-[360px] flex-col items-center gap-2 text-center">
          <S2Typography
            translatedContent={language.publishYourDashboard()}
            variant="headline-5-alt"
            textColorVariant="gray-1000"
          />
          <S2Typography
            translatedContent={language.publishYourDashboardDescription()}
            variant="body-7"
            textColorVariant="gray-700"
            dangerouslyOverrideClassNameFn={(className) =>
              cn(className, 'text-center')
            }
          />
        </div>
      </div>

      <div className="flex w-full gap-4">
        <SecondaryButtonLightBg
          className="flex-1"
          translatedContent={language.preview()}
          onClick={onPreview}
          disabled={isSaving}
          icon={() => (
            <ExternalLinkSvg
              svg={{ className: 'h-4 w-4 text-royal-900' }}
              wantsCurrentColor
            />
          )}
        />
        <PrimaryButtonLightBg
          className="flex-1"
          translatedContent={language.publish()}
          onClick={onPublish}
          disabled={isSaving}
          testId="publish-dashboard-cta"
          icon={() =>
            isSaving ? <Spinner size={17} textColor="text-white" /> : undefined
          }
        />
      </div>
    </div>
  );
};

const PublishDashboardLiveState = ({
  publicDashboardUrl,
  iframeCode,
  isSaving,
  onCopyUrl,
  onCopyEmbedCode,
  onViewDashboard,
  onUnpublish,
}: {
  publicDashboardUrl: string;
  iframeCode: string;
  isSaving: boolean;
  onCopyUrl: () => void;
  onCopyEmbedCode: () => void;
  onViewDashboard: () => void;
  onUnpublish: () => void;
}) => {
  const language = useLanguage();

  return (
    <div className="flex flex-1 flex-col gap-6">
      <div className="flex flex-col gap-2">
        <S2Typography
          translatedContent={language.copyUrl()}
          variant="headline-7-alt"
          textColorVariant="gray-1000"
        />
        <div className="flex h-10 items-center gap-4 rounded-[4px] border-[1.5px] border-gray-300 bg-gray-100 px-4">
          <S2Typography
            translatedContent={publicDashboardUrl}
            variant="body-7"
            textColorVariant="gray-700"
            truncate
            flex
          />
          <TouchTarget
            className="shrink-0"
            behavior={{
              type: 'onClick',
              onClick: onCopyUrl,
            }}
            content={() => <IconLink16Svg />}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2">
        <S2Typography
          translatedContent={language.embedDashboard()}
          variant="headline-7-alt"
          textColorVariant="gray-1000"
        />
        <div className="relative flex min-h-[270px] flex-1 rounded-[4px] border-[1.5px] border-gray-300 bg-gray-100 p-4">
          <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all text-xs leading-6 text-gray-700">
            <code>{iframeCode.replaceAll(' ', '\n  ')}</code>
          </pre>
          <TouchTarget
            className="absolute right-4 top-4 shrink-0"
            behavior={{
              type: 'onClick',
              onClick: onCopyEmbedCode,
            }}
            content={() => (
              <ContentCopyIcon className="h-4 w-4 text-royal-700" />
            )}
          />
        </div>
      </div>

      <div className="flex w-full items-center justify-between gap-4">
        <TouchTarget
          behavior={{
            type: 'onClick',
            onClick: onViewDashboard,
          }}
          content={() => (
            <div className="flex items-center gap-1">
              <ExternalLinkSvg
                svg={{ className: 'h-4 w-4 text-royal-700' }}
                wantsCurrentColor
              />
              <S2Typography
                translatedContent={language.viewDashboard()}
                variant="headline-7-alt"
                textColorVariant="royal-700"
              />
            </div>
          )}
        />
        <PrimaryButtonLightBg
          translatedContent={language.unpublish()}
          onClick={onUnpublish}
          disabled={isSaving}
          testId="unpublish-dashboard-cta"
          icon={() =>
            isSaving ? <Spinner size={17} textColor="text-white" /> : undefined
          }
        />
      </div>
    </div>
  );
};

const PublishTabContent = ({
  isPublic,
  publicDashboardUrl,
  iframeCode,
  isSaving,
  onCopyDashboardLink,
  onCopyEmbedCode,
  onOpenPublicDashboard,
  onUnpublishFromPublishTab,
  onPublishFromPublishTab,
}: {
  isPublic: boolean;
  publicDashboardUrl: string;
  iframeCode: string;
  isSaving: boolean;
  onCopyDashboardLink: () => void;
  onCopyEmbedCode: () => void;
  onOpenPublicDashboard: () => void;
  onUnpublishFromPublishTab: () => void;
  onPublishFromPublishTab: () => void;
}) => {
  if (isPublic) {
    return (
      <PublishDashboardLiveState
        publicDashboardUrl={publicDashboardUrl}
        iframeCode={iframeCode}
        isSaving={isSaving}
        onCopyUrl={onCopyDashboardLink}
        onCopyEmbedCode={onCopyEmbedCode}
        onViewDashboard={onOpenPublicDashboard}
        onUnpublish={onUnpublishFromPublishTab}
      />
    );
  }

  return (
    <PublishDashboardDraftState
      isSaving={isSaving}
      onPreview={onOpenPublicDashboard}
      onPublish={onPublishFromPublishTab}
    />
  );
};

const ConfirmUnpublishDashboardContent = ({
  isSaving,
  onKeepPublished,
  onConfirmUnpublish,
}: {
  isSaving: boolean;
  onKeepPublished: () => void;
  onConfirmUnpublish: () => void;
}) => {
  const language = useLanguage();

  return (
    <div className="flex h-full flex-col justify-between gap-6 px-4 py-4">
      <div className="flex flex-col gap-2">
        <S2Typography
          translatedContent={language.confirmUnpublishDashboardTitle()}
          variant="headline-3"
          textColorVariant="gray-1000"
          dangerouslyOverrideClassNameFn={(className) =>
            cn(
              className,
              'text-[32px] leading-[1.2] font-normal text-black [font-family:Georgia,Times_New_Roman,Times,serif]',
            )
          }
        />
        <S2Typography
          translatedContent={language.confirmUnpublishDashboardDescription()}
          variant="body-6"
          textColorVariant="gray-600"
          dangerouslyOverrideClassNameFn={(className) =>
            className
              .replace('text-sm', 'text-[16px]')
              .replace('leading-normal', 'leading-[1.4]')
              .replace('text-gray-600', 'text-[#52575B]')
              .replace(
                'font-sans',
                '[font-family:Plus_Jakarta_Sans,Karla,Trebuchet_MS,ui-sans-serif,sans-serif]',
              )
          }
        />
      </div>
      <div className="flex flex-col items-center gap-4">
        <PrimaryButtonLightBg
          className="w-full !bg-indigo-1000 hover:!bg-indigo-900"
          translatedContent={language.keepPublished()}
          onClick={onKeepPublished}
          disabled={isSaving}
        />
        <TertiaryButton
          label={language.confirmUnpublishDashboardAction()}
          disabled={isSaving}
          behavior={{
            type: 'onClick',
            onClick: onConfirmUnpublish,
          }}
        />
      </div>
    </div>
  );
};

export interface PublishDashboardModalProps {
  dashboardId: string;
  dashboard: DraftDashboard;
  dashboardOwnerEmail?: string | null;
  showDownloadDashboardButton?: boolean;
  canDownloadDashboard?: boolean;
  onDownloadDashboard?: (onComplete?: () => void) => void;
  downloadPending?: boolean;
  renderTrigger: (props: { onClick: () => void }) => React.ReactNode;
}

export const PublishDashboardModal = (props: PublishDashboardModalProps) => {
  const {
    dashboard,
    dashboardId,
    dashboardOwnerEmail = null,
    showDownloadDashboardButton = false,
    canDownloadDashboard = true,
    onDownloadDashboard,
    downloadPending = false,
    renderTrigger,
  } = props;

  const { myObservation } = useObservation();
  const { userAuthenticatedApiCall } = useUser();

  const canPublishDashboards = useMemo(() => {
    return isEntitledToPublishDashboards({
      features: myObservation?.features ?? [],
    });
  }, [myObservation?.features]);
  const isPublishDashboardsEnabled = useMemo(
    () =>
      isPublishDashboardsEnabledForRevision({ revisionFlags: myObservation }),
    [myObservation],
  );
  const canUsePublishTab = canPublishDashboards && isPublishDashboardsEnabled;

  const canInviteExternalUsersToDashboards = useMemo(() => {
    return isInviteExternalUsersToDashboardsEnabledForRevision({
      revisionFlags: myObservation,
    });
  }, [myObservation]);
  const canUseExternalUsersSharing = useMemo(
    () => canPublishDashboards && canInviteExternalUsersToDashboards,
    [canInviteExternalUsersToDashboards, canPublishDashboards],
  );

  const language = useLanguage();
  const selectedOrganizationId = useSelectedOrganizationId();
  const selectedDatabaseId = useSelectedDatabaseId();
  const selectedDatabaseRevisionId = useSelectedDatabaseRevisionId();

  const { selectedOrganization } = useContext(OrgSwitcherContext);

  const [showModal, setShowModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showConfirmUnpublishModal, setShowConfirmUnpublishModal] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PublishModalTab>('share');
  const resolvedActiveTab: PublishModalTab = canUsePublishTab
    ? activeTab
    : 'share';
  const [locallyRemovedExternalUserIds, setLocallyRemovedExternalUserIds] =
    useState<string[]>([]);
  const [resendingExternalUserId, setResendingExternalUserId] = useState<
    string | null
  >(null);
  const [removingExternalUserIds, setRemovingExternalUserIds] = useState<
    string[]
  >([]);
  const [shareTabMeasuredHeight, setShareTabMeasuredHeight] = useState<
    number | null
  >(null);
  const publishModalBodyRef = useRef<HTMLDivElement | null>(null);
  const {
    shareFlowMode,
    setShareFlowMode,
    externalInviteDraft,
    setExternalInviteDraft,
    externalInviteEmails,
    externalInviteFocused,
    externalInviteMessage,
    setExternalInviteMessage,
    externalInviteInputRef,
    hasInvalidExternalInviteEmail,
    isExternalInviteShareDisabled,
    isShowingExternalInviteComposePanel,
    handleExternalInviteKeyDown,
    handleExternalInviteBlur,
    handleExternalInvitePaste,
    handleStartExternalInviteComposer,
    handleProceedToExternalInviteConfirmation,
    handleRemoveExternalInviteEmail,
    resetExternalInviteFlow,
    hideExternalInviteComposer,
  } = useExternalInviteFlow();

  const handleToggleShowModal = useCallback(() => {
    setShowModal(!showModal);
    // Reset embed modal state when closing the main modal
    if (showModal) {
      setError(null);
      setWarning(null);
      setShowDownloadModal(false);
      setShowConfirmUnpublishModal(false);
      setActiveTab('share');
      setLocallyRemovedExternalUserIds([]);
      resetExternalInviteFlow();
      setResendingExternalUserId(null);
      setRemovingExternalUserIds([]);
      setShareTabMeasuredHeight(null);
    }
  }, [resetExternalInviteFlow, showModal]);

  useLayoutEffect(() => {
    if (!showModal || resolvedActiveTab === 'publish') {
      return;
    }

    const publishModalBody = publishModalBodyRef.current;
    if (!publishModalBody) {
      return;
    }

    const updateMeasuredHeight = () => {
      setShareTabMeasuredHeight(
        Math.ceil(publishModalBody.getBoundingClientRect().height),
      );
    };

    updateMeasuredHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateMeasuredHeight();
    });

    resizeObserver.observe(publishModalBody);

    return () => {
      resizeObserver.disconnect();
    };
  }, [resolvedActiveTab, showModal]);

  const [isPublic, setIsPublic] = useState(dashboard.isPublic);

  const { updateDashboard } = useDashboards();

  const updateDashboardPublicState = useCallback(
    async (nextIsPublic: boolean) => {
      setIsSaving(true);
      setError(null);

      const result = await updateDashboard(dashboardId, {
        ...dashboard,
        isPublic: nextIsPublic,
      });

      setIsSaving(false);

      if (!result) {
        setError(language.unknownError());
        return;
      }

      setIsPublic(nextIsPublic);
    },
    [dashboard, dashboardId, language, updateDashboard],
  );

  const publicDashboardUrl = useMemo(() => {
    return `${window.location.origin}/shared/dashboards/${dashboardId}`;
  }, [dashboardId]);

  const handleCopyDashboardLink = useCallback(() => {
    void navigator.clipboard.writeText(publicDashboardUrl);
  }, [publicDashboardUrl]);

  const handleOpenPublicDashboard = useCallback(() => {
    window.open(publicDashboardUrl, '_blank', 'noopener,noreferrer');
  }, [publicDashboardUrl]);

  const handleOpenGuestDashboard = useCallback(() => {
    window.open(publicDashboardUrl, '_blank', 'noopener,noreferrer');
  }, [publicDashboardUrl]);

  const handlePublishFromPublishTab = useCallback(() => {
    void updateDashboardPublicState(true);
  }, [updateDashboardPublicState]);

  const handleUnpublishFromPublishTab = useCallback(() => {
    setShowConfirmUnpublishModal(true);
  }, []);

  const handleCloseConfirmUnpublishModal = useCallback(() => {
    setShowConfirmUnpublishModal(false);
  }, []);

  const handleConfirmUnpublishDashboard = useCallback(() => {
    setShowConfirmUnpublishModal(false);
    void updateDashboardPublicState(false);
  }, [updateDashboardPublicState]);

  const handleToggleDownloadModal = useCallback(() => {
    setShowDownloadModal(!showDownloadModal);
  }, [showDownloadModal]);

  const handleDownloadDashboard = useCallback(() => {
    if (!canDownloadDashboard || !onDownloadDashboard || downloadPending) {
      return;
    }

    onDownloadDashboard(() => {
      setShowDownloadModal(false);
      setShowModal(false);
      setError(null);
    });
  }, [canDownloadDashboard, downloadPending, onDownloadDashboard]);

  const iframeCode = useMemo(() => {
    return `<iframe src="${publicDashboardUrl}" width="100%" height="100%"/>`;
  }, [publicDashboardUrl]);

  const handleCopyEmbedCode = useCallback(() => {
    void navigator.clipboard.writeText(iframeCode);
  }, [iframeCode]);

  const truncatedOrganizationName = useMemo(() => {
    return truncateTextIfNecessary({
      text: selectedOrganization?.name ?? '',
      maxChars: 40,
    });
  }, [selectedOrganization?.name]);

  const generalAccessTrigger = useMemo(() => {
    return (
      <div className="flex min-w-0 items-center">
        <S2Typography
          translatedContent={truncatedOrganizationName}
          variant="headline-6-alt"
          textColorVariant="gray-1000"
          dangerouslyOverrideClassNameFn={(className) =>
            cn(className, 'truncate whitespace-nowrap')
          }
        />
      </div>
    );
  }, [truncatedOrganizationName]);

  const getDashboardExternalUsersUrl = useCallback(() => {
    if (
      !showModal ||
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId
    ) {
      return null;
    }

    return `${getApiBaseUrl()}/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}/dashboards/${dashboardId}/external-users`;
  }, [
    dashboardId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    showModal,
  ]);

  const isDashboardExternalUsersRequestReady = Boolean(
    showModal &&
      selectedOrganizationId &&
      selectedDatabaseId &&
      selectedDatabaseRevisionId,
  );

  const externalUsersReadPattern = useSimpleReadPattern({
    getUrlFn: getDashboardExternalUsersUrl,
    canRead: canUseExternalUsersSharing && isDashboardExternalUsersRequestReady,
    responseSchema: DashboardExternalUsersDto,
  });

  const inviteExternalUsersPattern = useSimpleWritePattern({
    getUrlFn: getDashboardExternalUsersUrl,
    canWrite:
      canUseExternalUsersSharing && isDashboardExternalUsersRequestReady,
    writeSchema: InviteDashboardExternalUsersRequestDto,
    responseSchema: InviteDashboardExternalUsersResponseDto,
    method: 'POST',
  });

  const previousShowModalRef = useRef(showModal);
  const shouldReloadExternalUsersAfterLoadRef = useRef(false);

  useEffect(() => {
    const wasModalOpen = previousShowModalRef.current;

    previousShowModalRef.current = showModal;

    if (!showModal || wasModalOpen) {
      return;
    }

    if (
      externalUsersReadPattern.status === 'done' ||
      externalUsersReadPattern.status === 'error'
    ) {
      externalUsersReadPattern.reloadEntity();
    }
  }, [externalUsersReadPattern, showModal]);

  const activeExternalUsers = useMemo(() => {
    const externalUsersEntity =
      externalUsersReadPattern.status === 'done'
        ? externalUsersReadPattern.entity
        : null;

    if (!externalUsersEntity) {
      return [];
    }

    return externalUsersEntity
      .filter((user) => !locallyRemovedExternalUserIds.includes(user.id))
      .filter((user) => user.state === 'active')
      .sort((left, right) => left.email.localeCompare(right.email));
  }, [externalUsersReadPattern, locallyRemovedExternalUserIds]);

  const canManageExternalUsers =
    canUseExternalUsersSharing && isDashboardExternalUsersRequestReady;

  const ownerEmail = dashboardOwnerEmail ?? null;

  const reloadExternalUsers = useCallback(() => {
    if (
      externalUsersReadPattern.status === 'done' ||
      externalUsersReadPattern.status === 'error'
    ) {
      externalUsersReadPattern.reloadEntity();
      return;
    }

    if (externalUsersReadPattern.status === 'loading') {
      shouldReloadExternalUsersAfterLoadRef.current = true;
    }
  }, [externalUsersReadPattern]);

  useEffect(() => {
    if (
      !shouldReloadExternalUsersAfterLoadRef.current ||
      (externalUsersReadPattern.status !== 'done' &&
        externalUsersReadPattern.status !== 'error')
    ) {
      return;
    }

    shouldReloadExternalUsersAfterLoadRef.current = false;
    externalUsersReadPattern.reloadEntity();
  }, [externalUsersReadPattern]);

  const buildExternalUserActionUrl = useCallback(
    (dashboardExternalUserId: string, actionPath: string) => {
      if (
        !selectedOrganizationId ||
        !selectedDatabaseId ||
        !selectedDatabaseRevisionId
      ) {
        return null;
      }

      return `${getApiBaseUrl()}/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}/dashboards/${dashboardId}/external-users/${dashboardExternalUserId}/${actionPath}`;
    },
    [
      dashboardId,
      selectedDatabaseId,
      selectedDatabaseRevisionId,
      selectedOrganizationId,
    ],
  );

  const handleShareExternalUsers = useCallback(() => {
    if (
      !canUseExternalUsersSharing ||
      inviteExternalUsersPattern.status === 'saving' ||
      externalInviteEmails.length === 0
    ) {
      return;
    }

    setError(null);
    setWarning(null);

    if (externalUsersReadPattern.status === 'loading') {
      shouldReloadExternalUsersAfterLoadRef.current = true;
    }

    inviteExternalUsersPattern.saveEntity(
      {
        emails: externalInviteEmails,
        message: externalInviteMessage.trim() || null,
      },
      (inviteResult) => {
        if (inviteResult.failedInvitationEmails.length > 0) {
          setWarning(
            getDashboardExternalInviteWarning({
              failedEmails: inviteResult.failedInvitationEmails,
              language,
            }),
          );
        }
        resetExternalInviteFlow();
        setLocallyRemovedExternalUserIds([]);
        reloadExternalUsers();
      },
      (inviteError: BaseError) => {
        setError(resolveBaseErrorMessage(inviteError, language.unknownError()));
      },
    );
  }, [
    canUseExternalUsersSharing,
    inviteExternalUsersPattern,
    externalInviteEmails,
    externalUsersReadPattern.status,
    externalInviteMessage,
    resetExternalInviteFlow,
    reloadExternalUsers,
    language,
  ]);

  const handleResendExternalUserInvitation = useCallback(
    async (dashboardExternalUserId: string) => {
      if (resendingExternalUserId || !canManageExternalUsers) {
        return;
      }

      const url = buildExternalUserActionUrl(
        dashboardExternalUserId,
        'resend-invitation',
      );
      if (!url) return;

      setError(null);
      setResendingExternalUserId(dashboardExternalUserId);

      const result = await userAuthenticatedApiCall({
        responseSchema: DashboardExternalUserDto,
        url,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(
            ResendDashboardExternalUserInvitationRequestDto.parse({
              message: null,
            }),
          ),
        },
      });

      setResendingExternalUserId(null);

      if (!result || result.resultType === 'err') {
        setError(
          resolveBaseErrorMessage(result?.error, language.unknownError()),
        );
        return;
      }
    },
    [
      buildExternalUserActionUrl,
      canManageExternalUsers,
      language,
      resendingExternalUserId,
      userAuthenticatedApiCall,
    ],
  );

  const handleExternalInviteDraftChange = useCallback(
    (value: string) => {
      setError(null);
      setWarning(null);
      setExternalInviteDraft(value);
    },
    [setExternalInviteDraft],
  );

  const handleRemoveExternalInviteEmailWithErrorReset = useCallback(
    (email: string) => {
      setError(null);
      setWarning(null);
      handleRemoveExternalInviteEmail(email);
    },
    [handleRemoveExternalInviteEmail],
  );

  const handleProceedToExternalInviteConfirmationWithErrorReset =
    useCallback(() => {
      setError(null);
      setWarning(null);
      handleProceedToExternalInviteConfirmation();
    }, [handleProceedToExternalInviteConfirmation]);

  const handleRemoveExternalUser = useCallback(
    async (dashboardExternalUserId: string) => {
      if (!canManageExternalUsers) {
        return;
      }

      if (removingExternalUserIds.includes(dashboardExternalUserId)) {
        return;
      }

      if (!window.confirm(language.confirmRemoveExternalUser())) {
        return;
      }

      const url = buildExternalUserActionUrl(dashboardExternalUserId, 'revoke');
      if (!url) return;

      setError(null);
      setRemovingExternalUserIds((previousIds) => {
        if (previousIds.includes(dashboardExternalUserId)) {
          return previousIds;
        }

        return [...previousIds, dashboardExternalUserId];
      });

      const result = await userAuthenticatedApiCall({
        responseSchema: EmptyObject,
        url,
        requestOptions: {
          method: 'POST',
        },
      });

      setRemovingExternalUserIds((previousIds) =>
        previousIds.filter((id) => id !== dashboardExternalUserId),
      );

      if (!result || result.resultType === 'err') {
        setError(
          resolveBaseErrorMessage(result?.error, language.unknownError()),
        );
        return;
      }

      setLocallyRemovedExternalUserIds((previousIds) => {
        if (previousIds.includes(dashboardExternalUserId)) {
          return previousIds;
        }

        return [...previousIds, dashboardExternalUserId];
      });
    },
    [
      buildExternalUserActionUrl,
      canManageExternalUsers,
      language,
      removingExternalUserIds,
      userAuthenticatedApiCall,
    ],
  );

  return (
    <>
      <LightModal
        width="w-[480px] max-h-[680px]"
        showHeaderDivider={false}
        contentClassName="flex min-h-0 flex-1 flex-col pt-4 pb-0"
        showModal={
          showModal && !showDownloadModal && !showConfirmUnpublishModal
        }
        onClose={handleToggleShowModal}
        title={
          <div className="flex min-w-0 items-center gap-4">
            <IconShareSvg
              svg={{ className: 'h-4 w-4 shrink-0 text-purple-900' }}
            />
            <S2Typography
              translatedContent={dashboard.name}
              variant="headline-5"
              textColorVariant="gray-1000"
              truncate
              flex
            />
            {resolvedActiveTab === 'publish' && isPublic ? (
              <div className="flex shrink-0 items-center gap-1 rounded-[4px] border border-[#D0E8CA] bg-[#F2FAF0] px-2 py-1">
                <div className="h-1 w-1 rounded-full bg-green-600" />
                <S2Typography
                  translatedContent={language.live()}
                  variant="small-label"
                  textColorVariant="green-600"
                  dangerouslyOverrideClassNameFn={(className) =>
                    cn(className, 'font-bold')
                  }
                />
              </div>
            ) : null}
          </div>
        }
      >
        <>
          <div
            ref={publishModalBodyRef}
            className={cn('flex min-h-0 flex-col gap-8', {
              'min-h-[520px]':
                shareFlowMode === 'confirm' ||
                isShowingExternalInviteComposePanel,
            })}
            style={
              resolvedActiveTab === 'publish' && shareTabMeasuredHeight
                ? { minHeight: `${shareTabMeasuredHeight}px` }
                : undefined
            }
            data-testid="publish-modal"
          >
            {canUsePublishTab ? (
              <PublishModalTabs
                activeTab={resolvedActiveTab}
                showPublishTab={canUsePublishTab}
                onSelectTab={(tab) => {
                  if (tab === 'publish' && !canUsePublishTab) {
                    return;
                  }

                  setActiveTab(tab);
                  if (tab === 'publish') {
                    hideExternalInviteComposer();
                  }
                }}
              />
            ) : null}

            {error ? (
              <S2Typography
                translatedContent={error}
                variant="body-7"
                textColorVariant="red-600"
              />
            ) : null}

            {warning ? (
              <S2Typography
                translatedContent={warning}
                variant="body-7"
                textColorVariant="gray-1000"
                dangerouslyOverrideClassNameFn={(className) =>
                  cn(className, 'text-yellow-700 whitespace-normal break-all')
                }
              />
            ) : null}

            {resolvedActiveTab !== 'publish' ? (
              <ShareTabContent
                shareState={{
                  flowMode: shareFlowMode,
                  dashboardName: dashboard.name,
                  canInviteExternalUsers: canUseExternalUsersSharing,
                  isSharingExternalUsers:
                    inviteExternalUsersPattern.status === 'saving',
                  isShowingComposePanel: isShowingExternalInviteComposePanel,
                  message: externalInviteMessage,
                  isShareDisabled: isExternalInviteShareDisabled,
                }}
                inviteInput={{
                  emails: externalInviteEmails,
                  draft: externalInviteDraft,
                  focused: externalInviteFocused,
                  hasValidationError: hasInvalidExternalInviteEmail,
                  inputRef: externalInviteInputRef,
                  onStartComposer: handleStartExternalInviteComposer,
                  onDraftChange: handleExternalInviteDraftChange,
                  onBlur: handleExternalInviteBlur,
                  onKeyDown: handleExternalInviteKeyDown,
                  onPaste: handleExternalInvitePaste,
                  onRemoveEmail: handleRemoveExternalInviteEmailWithErrorReset,
                }}
                accessSections={{
                  externalUsersStatus: externalUsersReadPattern.status,
                  activeExternalUsers,
                  showPeopleWithAccessSection: canUseExternalUsersSharing,
                  ownerEmail,
                  resendingExternalUserId,
                  removingExternalUserIds,
                  onOpenGuestDashboard: handleOpenGuestDashboard,
                  generalAccessTrigger,
                  language,
                  onResendExternalUser: (dashboardExternalUserId) => {
                    void handleResendExternalUserInvitation(
                      dashboardExternalUserId,
                    );
                  },
                  onRemoveExternalUser: (dashboardExternalUserId) => {
                    void handleRemoveExternalUser(dashboardExternalUserId);
                  },
                }}
                shareActions={{
                  onGoBackFromConfirmation: () => {
                    setShareFlowMode('default');
                    externalInviteInputRef.current?.focus();
                  },
                  onPreviewGuestDashboard: handleOpenGuestDashboard,
                  onShareExternalUsers: handleShareExternalUsers,
                  onMessageChange: setExternalInviteMessage,
                  onProceedToConfirmation:
                    handleProceedToExternalInviteConfirmationWithErrorReset,
                  copyDashboardLink: window.location.href,
                  copyToastText: language.linkCopiedToast(),
                }}
              />
            ) : (
              <PublishTabContent
                isPublic={isPublic}
                publicDashboardUrl={publicDashboardUrl}
                iframeCode={iframeCode}
                isSaving={isSaving}
                onCopyDashboardLink={handleCopyDashboardLink}
                onCopyEmbedCode={handleCopyEmbedCode}
                onOpenPublicDashboard={handleOpenPublicDashboard}
                onUnpublishFromPublishTab={handleUnpublishFromPublishTab}
                onPublishFromPublishTab={handlePublishFromPublishTab}
              />
            )}
          </div>
          {showDownloadDashboardButton ? (
            <div
              className="flex absolute "
              style={{ top: 'calc(100% + 8px)', left: 0, right: 0 }}
            >
              <div className="w-full overflow-hidden rounded-2xl bg-white">
                <>
                  <TouchTarget
                    content={() => (
                      <div className="flex items-center justify-between px-6 py-5">
                        <S2Typography
                          translatedContent={language.downloadDashboard()}
                          variant="headline-7-alt"
                          textColorVariant="gray-1000"
                        />
                        <IconChevronRightSvg
                          svg={{ className: 'h-6 w-6 text-gray-1000' }}
                        />
                      </div>
                    )}
                    behavior={{
                      type: 'onClick',
                      onClick: handleToggleDownloadModal,
                    }}
                  />
                </>
              </div>
            </div>
          ) : null}
        </>
      </LightModal>
      <LightModal
        width="w-full max-w-md min-w-[443px]"
        showModal={showDownloadModal}
        onClose={handleToggleDownloadModal}
        title={
          <div className="flex items-center gap-4">
            <TouchTarget
              content={() => (
                <IconArrowLeft24pxSvg
                  svg={{ className: 'w-6 h-6 text-gray-600 ' }}
                />
              )}
              behavior={{ type: 'onClick', onClick: handleToggleDownloadModal }}
            />

            <S2Typography
              translatedContent={language.downloadDashboard()}
              variant="headline-4"
              textColorVariant="gray-1000"
              dangerouslyOverrideClassNameFn={(className) =>
                className
                  .replace('font-medium', 'font-normal')
                  .replace('leading-normal', 'leading-[1.3]')
              }
            />
          </div>
        }
        actions={
          <div className="flex w-full items-center justify-end pt-4">
            <PrimaryButtonLightBg
              translatedContent={language.downloadAnalysis()}
              onClick={handleDownloadDashboard}
              disabled={
                !canDownloadDashboard || !onDownloadDashboard || downloadPending
              }
              innerClassName="h-9 min-w-[80px] gap-1 px-3 py-2"
              icon={() =>
                downloadPending ? (
                  <CircularProgress
                    size={16}
                    thickness={4}
                    color="inherit"
                    className="text-gray-400"
                  />
                ) : (
                  <IconDownloadSvg svg={{ className: 'h-4 w-4 text-white' }} />
                )
              }
            />
          </div>
        }
      >
        <div className="flex flex-col">
          <S2Typography
            translatedContent={language.downloadDashboardAsFile({
              dashboardName: dashboard.name,
            })}
            variant="body-6"
            textColorVariant="gray-1000"
            dangerouslyOverrideClassNameFn={(className) =>
              className
                .replace('text-sm', 'text-[16px]')
                .replace('leading-normal', 'leading-[1.5]')
                .concat(' tracking-[-0.16px]')
            }
          />
          <div className="mt-4 flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-[48px] bg-[#E7E8E8] p-2">
              <IconDocumentFileSvg
                svg={{ className: 'h-4 w-4 text-gray-900' }}
              />
            </div>
            <div className="flex flex-col">
              <S2Typography
                translatedContent={language.documentFile()}
                variant="headline-7-alt"
                textColorVariant="gray-1000"
              />
              <S2Typography
                translatedContent={language.downloadDashboardDocxDescription()}
                variant="small-label"
                textColorVariant="gray-600"
              />
            </div>
          </div>
        </div>
      </LightModal>
      <LightModal
        width="w-[443px] h-[370px]"
        showHeaderDivider={false}
        contentClassName="pt-0 pb-0"
        showCloseButton={false}
        panelClassName="px-6 pt-6 pb-4"
        showModal={showConfirmUnpublishModal}
        onClose={handleCloseConfirmUnpublishModal}
        title={<div />}
      >
        <ConfirmUnpublishDashboardContent
          isSaving={isSaving}
          onKeepPublished={handleCloseConfirmUnpublishModal}
          onConfirmUnpublish={handleConfirmUnpublishDashboard}
        />
      </LightModal>
      {renderTrigger({ onClick: handleToggleShowModal })}
    </>
  );
};
